import React, { useMemo } from 'react';

import type { CompositionV3DataQuery } from '@revelio/data-access';
import {
  FormatType,
  getFormatter,
  getOtherColor,
  ReplotChartNames,
  StackedBarChart,
  type StackedBarData,
  useChartTheme,
} from '@revelio/replots';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../filters/types/filters';
import {
  getTreemapDrilldownIsLeafLevel,
  getTreemapInitialLevelOffset,
  isTreemapDrilldownSegment,
  type TreemapDrilldownSegment,
} from '../../filters/utils/treemapDrilldownLevels';
import { usePlotMetric } from '../../hooks/usePlotMetric';
import { convertCompositionDataV3 } from '../DataProvider/composition/composition-v3/convert-composition-data-v3';
import { NestedCompositionTreemapDrilldownProvider } from '../DataProvider/composition/NestedCompositionTreemapDrilldownProvider';
import {
  type BreakdownSingleDataSet,
  type BreakdownTimeseriesDataSet,
  DataProviderQueryStatus,
  DataType,
  isLeveledBreakdownDataSet,
} from '../DataProvider/types';
import { routesToComposition } from '../DataProvider/utils/data-provider-routing';
import { tableToBreakdownSingleDataSet } from '../DataProvider/utils/table-to-breakdown-single-data-set';
import { findLadderLevel } from '../DataResolver/taxonomy-levels';
import { useCompositionV4Drilldown } from '../DataResolver/use-composition-v4-drilldown';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import { useChartHeaderBreadcrumbSetter } from './ChartHeaderBreadcrumb';
import { ChartLoader } from './ChartLoader';
import { useChart } from './ChartProvider';
import { usePlotShareData } from './chart-menu/plot-share/usePlotShare';
import styles from './stacked-bar.module.css';
import { buildStackedBarTimeseriesData } from './utils/build-stacked-bar-timeseries-data';
import {
  COMPOSITION_DRILLDOWN_OTHER_COLOR,
  COMPOSITION_DRILLDOWN_OTHER_COLOR_DARK,
  type DrilldownEntry,
  drilldownEntryToFilter,
  getDrilldownColor,
} from './utils/composition-drilldown';
import { isOtherSegment } from './utils/group-and-filter-data';

type CompositionV3Payload = NonNullable<
  CompositionV3DataQuery['composition_v3']
>;

function getStackedBarFormat(
  plotFormat: FormatType | undefined,
  metric?: MetricValues
): FormatType {
  if (plotFormat !== undefined) {
    return plotFormat;
  }
  switch (metric) {
    case 'headcount':
      return FormatType.INTEGER;

    case 'tenure':
      return FormatType.YEAR;

    case 'base_salary':
    case 'total_compensation':
    case 'contractor_pay_rate':
    case 'total_spend_salary':
    case 'total_spend_compensation':
      return FormatType.CURRENCY;

    default:
      return FormatType.INTEGER;
  }
}

type StackedBarViewProps = {
  data: BreakdownSingleDataSet | BreakdownTimeseriesDataSet;
  metric?: MetricValues;
  hideLegend?: boolean;
  colorPalette?: string[];
  colorOverride?: Record<string, string>;
  onSegmentClick?: (label: string, color?: string) => void;
};

const StackedBarView = ({
  data,
  metric,
  hideLegend = false,
  colorPalette: colorPaletteProp,
  colorOverride: colorOverrideProp,
  onSegmentClick,
}: StackedBarViewProps) => {
  const { item } = useChart();
  const colorPalette = colorPaletteProp ?? item?.plot?.colorPalette;
  const { code } = useCurrencyPreference();

  const isTimeseries = data.type === DataType.BREAKDOWN_TIMESERIES;

  const stackedBarChartData: StackedBarData[] = useMemo(
    () =>
      data.type === DataType.BREAKDOWN_TIMESERIES
        ? buildStackedBarTimeseriesData(data.data)
        : data.data.map((entity) => ({
            group: entity.dim,
            values: (entity.breakdown ?? []).map((b) => ({
              label: b.dim,
              value: b.value ?? 0,
              id: b.id,
            })),
          })),
    [data]
  );

  const segmentOrder = useMemo(() => {
    const seen = new Set<string>();
    const nonOther: string[] = [];
    const other: string[] = [];
    const pushSegment = (segment: { dim: string; id?: string | null }) => {
      if (seen.has(segment.dim)) return;
      seen.add(segment.dim);
      if (isOtherSegment(segment)) other.push(segment.dim);
      else nonOther.push(segment.dim);
    };
    if (data.type === DataType.BREAKDOWN_TIMESERIES) {
      data.data.forEach((segment) => {
        pushSegment({ dim: segment.dim, id: segment.id });
      });
    } else {
      data.data.forEach((entity) => {
        (entity.breakdown ?? []).forEach((b) => {
          pushSegment({ dim: b.dim, id: b.id });
        });
      });
    }
    return [...nonOther, ...other];
  }, [data]);

  const format = useMemo(
    () => getStackedBarFormat(item?.plot?.format, metric),
    [item?.plot?.format, metric]
  );

  const chartProps = useMemo(
    () => ({
      data: stackedBarChartData,
      hideLegend,
      colorOverride: colorOverrideProp ?? { Other: getOtherColor() },
      colorPalette,
      format,
      currencyCode: code,
      segmentOrder,
      orientation: item?.plot?.orientation ?? 'horizontal',
      isTimeseries,
      ...(isTimeseries && { highlightMatchingSegments: false }),
    }),
    [
      stackedBarChartData,
      hideLegend,
      colorOverrideProp,
      colorPalette,
      format,
      code,
      segmentOrder,
      item?.plot?.orientation,
      isTimeseries,
    ]
  );

  usePlotShareData({
    chartType: ReplotChartNames.StackedBarChartHorizontal,
    chartProps,
  });

  // The click handler stays out of chartProps so the share capture only sees
  // serialisable props (shared/exported copies don't drill).
  return <StackedBarChart {...chartProps} onSegmentClick={onSegmentClick} />;
};

type DrilldownStackedBarContentProps = {
  dataset: BreakdownSingleDataSet;
  metric: MetricValues;
  hideLegend?: boolean;
  colorPalette?: string[];
  colorOverride: Record<string, string>;
  /** Set while drilled: every non-Other category takes this colour. */
  drillColor?: string;
  format: FormatType;
  displayLevelId: string;
  isDisplayLeaf: boolean;
  onDrill: (
    levelId: string,
    cellId: string,
    cellName: string,
    color?: string
  ) => void;
  onTotalLabelChange: (label: string | null) => void;
};

function DrilldownStackedBarContent({
  dataset,
  metric,
  hideLegend,
  colorPalette,
  colorOverride,
  drillColor,
  format,
  displayLevelId,
  isDisplayLeaf,
  onDrill,
  onTotalLabelChange,
}: DrilldownStackedBarContentProps) {
  const idByLabel = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const entity of dataset.data) {
      for (const b of entity.breakdown ?? []) {
        const id = b.drilldownId ?? b.id;
        if (id != null && !map.has(b.dim)) map.set(b.dim, String(id));
      }
    }
    return map;
  }, [dataset]);

  // A cross-entity sum is meaningless for percentage formats.
  const totalLabel = React.useMemo(() => {
    if (format === FormatType.PERCENTAGE) return null;
    const total = dataset.data.reduce(
      (sum, entity) =>
        sum + (entity.breakdown ?? []).reduce((s, b) => s + (b.value ?? 0), 0),
      0
    );
    return getFormatter(format)(total);
  }, [dataset, format]);

  React.useEffect(() => {
    onTotalLabelChange(totalLabel);
  }, [totalLabel, onTotalLabelChange]);

  // The stacked-bar colour model assigns the palette by index (no cycling),
  // so the drilled uniform colour is applied as a per-label override instead
  // of a single-colour palette; "Other" keeps its grey.
  const effectiveColorOverride = React.useMemo(() => {
    if (drillColor === undefined) return colorOverride;
    const overrides: Record<string, string> = { ...colorOverride };
    for (const entity of dataset.data) {
      for (const b of entity.breakdown ?? []) {
        if (overrides[b.dim] !== undefined) continue;
        if (isOtherSegment({ dim: b.dim, id: b.id })) continue;
        overrides[b.dim] = drillColor;
      }
    }
    return overrides;
  }, [dataset, drillColor, colorOverride]);

  // useMemo (not useCallback) so the leaf level yields `undefined`, which is
  // what disables the chart's drill click/hover affordance.
  const handleSegmentClick = React.useMemo(() => {
    if (isDisplayLeaf) return undefined;
    return (label: string, color?: string) => {
      const cellId = idByLabel.get(label);
      if (cellId === undefined) return;
      if (isOtherSegment({ dim: label, id: cellId })) return;
      onDrill(displayLevelId, cellId, label, color);
    };
  }, [isDisplayLeaf, idByLabel, displayLevelId, onDrill]);

  return (
    <StackedBarView
      data={dataset}
      metric={metric}
      hideLegend={hideLegend}
      colorPalette={colorPalette}
      colorOverride={effectiveColorOverride}
      onSegmentClick={handleSegmentClick}
    />
  );
}

type CompositionV3DrilldownStackedBarContentProps = Omit<
  DrilldownStackedBarContentProps,
  'dataset'
> & {
  compositionData: CompositionV3Payload;
  monthRefs: string[];
  secondaryDimension: TreemapDrilldownSegment;
};

function CompositionV3DrilldownStackedBarContent({
  compositionData,
  monthRefs,
  secondaryDimension,
  ...props
}: CompositionV3DrilldownStackedBarContentProps) {
  const { metric } = props;
  const dataset = React.useMemo(() => {
    const converted = convertCompositionDataV3({
      metric: metric as Parameters<
        typeof convertCompositionDataV3
      >[0]['metric'],
      segment: secondaryDimension,
      data: compositionData,
      monthRefs,
    });
    return converted.type === DataType.BREAKDOWN_SINGLE ? converted : null;
  }, [metric, secondaryDimension, compositionData, monthRefs]);

  return dataset && <DrilldownStackedBarContent dataset={dataset} {...props} />;
}

type DrilldownStackedBarProps = {
  data: BreakdownSingleDataSet;
  metric: MetricValues;
  hideLegend?: boolean;
  secondaryDimension: TreemapDrilldownSegment;
  primaryDimension: PrimaryFilterId;
};

/**
 * Drilldown variant of the compositions stacked bar (themed appearances,
 * Figma 1096-21120/21201): clicking a segment drills the breakdown into that
 * category — the drilled view keeps the clicked category's colour ("Other"
 * stays grey) and the drill path renders as the chart-header breadcrumb.
 */
const DrilldownStackedBar = ({
  data,
  metric,
  hideLegend,
  secondaryDimension,
  primaryDimension,
}: DrilldownStackedBarProps) => {
  const { theme } = useChartTheme();
  const isDark = theme === 'dark';
  const { item } = useChart();
  const effectiveFilters = useEffectiveFilters();
  const { geographySelectionListIds } = useFilterContext();
  const setHeaderBreadcrumb = useChartHeaderBreadcrumbSetter();

  const [drilldownStack, setDrilldownStack] = React.useState<DrilldownEntry[]>(
    []
  );
  const [drillTotalLabel, setDrillTotalLabel] = React.useState<string | null>(
    null
  );

  const initialLevelOffset = getTreemapInitialLevelOffset(
    secondaryDimension,
    effectiveFilters,
    geographySelectionListIds
  );

  const geographyLadderKey = geographySelectionListIds.join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the drillable dimensions change
  React.useEffect(() => {
    setDrilldownStack([]);
    setDrillTotalLabel(null);
  }, [
    primaryDimension,
    secondaryDimension,
    initialLevelOffset,
    geographyLadderKey,
  ]);

  const handleBreadcrumbClick = React.useCallback((targetDepth: number) => {
    setDrillTotalLabel(null);
    setDrilldownStack((prev) => prev.slice(0, targetDepth));
  }, []);

  React.useEffect(() => {
    if (setHeaderBreadcrumb === null) return;
    if (drilldownStack.length === 0) {
      setHeaderBreadcrumb(null);
      return;
    }
    setHeaderBreadcrumb({
      path: drilldownStack.map((entry, index) => ({
        name: entry.name,
        targetDepth: index + 1,
      })),
      totalLabel: drillTotalLabel,
      onNavigate: handleBreadcrumbClick,
    });
  }, [
    setHeaderBreadcrumb,
    drilldownStack,
    drillTotalLabel,
    handleBreadcrumbClick,
  ]);

  React.useEffect(() => {
    if (setHeaderBreadcrumb === null) return;
    return () => setHeaderBreadcrumb(null);
  }, [setHeaderBreadcrumb]);

  const pushDrill = React.useCallback(
    (levelId: string, cellId: string, cellName: string, color?: string) => {
      const needsNumericId =
        secondaryDimension === 'geography' || secondaryDimension === 'rics';
      const id = needsNumericId ? parseInt(cellId, 10) : cellId;
      if (needsNumericId && !Number.isFinite(id as number)) return;
      setDrillTotalLabel(null);
      setDrilldownStack((prev) => {
        const top = prev[prev.length - 1];
        if (
          top !== undefined &&
          top.level === levelId &&
          String(top.id) === String(id)
        ) {
          return prev;
        }
        return [
          ...prev,
          {
            level: levelId,
            id,
            name: cellName,
            segment: secondaryDimension,
            color,
          },
        ];
      });
    },
    [secondaryDimension]
  );

  const drilldown = React.useMemo(
    () =>
      drilldownStack.length > 0 ? drilldownEntryToFilter(drilldownStack) : null,
    [drilldownStack]
  );
  const metrics = React.useMemo(() => [metric], [metric]);
  const drilled = useCompositionV4Drilldown({
    primaryDimension,
    dimension: secondaryDimension,
    drilledInto: drilldownStack[drilldownStack.length - 1],
    metrics,
  });
  const drilledData = React.useMemo(
    () =>
      drilled?.status === DataProviderQueryStatus.Success && drilled.data
        ? tableToBreakdownSingleDataSet(drilled.data, metric)
        : undefined,
    [drilled, metric]
  );
  const drillColor =
    drilldownStack.length > 0 ? getDrilldownColor(drilldownStack) : undefined;
  const colorPalette = item?.plot?.colorPalette;
  const colorOverride = React.useMemo(
    () => ({
      Other: isDark
        ? COMPOSITION_DRILLDOWN_OTHER_COLOR_DARK
        : COMPOSITION_DRILLDOWN_OTHER_COLOR,
    }),
    [isDark]
  );
  const format = getStackedBarFormat(item?.plot?.format, metric);
  const contentProps = {
    metric,
    hideLegend,
    colorPalette,
    colorOverride,
    drillColor,
    format,
    onDrill: pushDrill,
    onTotalLabelChange: setDrillTotalLabel,
  };

  const viewData = drilldown === null ? data : drilledData;
  const leveledData =
    viewData && isLeveledBreakdownDataSet(viewData) ? viewData : undefined;
  const levelId =
    leveledData &&
    findLadderLevel(leveledData.level, geographySelectionListIds)?.id;
  if (leveledData && (levelId !== undefined || drilldown !== null)) {
    return (
      <DrilldownStackedBarContent
        {...contentProps}
        dataset={leveledData}
        displayLevelId={levelId ?? ''}
        isDisplayLeaf={
          levelId === undefined ||
          getTreemapDrilldownIsLeafLevel(
            secondaryDimension,
            levelId,
            geographySelectionListIds
          )
        }
      />
    );
  }
  if (drilldown !== null && drilled) {
    return drilled.status === DataProviderQueryStatus.Fetching ||
      drilled.status === DataProviderQueryStatus.Idle ? (
      <div className={styles.drilldownLoaderRoot}>
        <ChartLoader />
      </div>
    ) : null;
  }

  return (
    <NestedCompositionTreemapDrilldownProvider
      primaryDimension={primaryDimension}
      segment={secondaryDimension}
      drilldown={drilldown}
      nestingEnabled={false}
    >
      {({
        data: drillData,
        monthRefs,
        status,
        displayLevelId,
        isDisplayLeaf,
      }) => {
        if (status === DataProviderQueryStatus.Fetching) {
          return (
            <div className={styles.drilldownLoaderRoot}>
              <ChartLoader />
            </div>
          );
        }
        if (drillData === undefined) return null;
        return (
          <CompositionV3DrilldownStackedBarContent
            {...contentProps}
            compositionData={drillData}
            monthRefs={monthRefs}
            secondaryDimension={secondaryDimension}
            displayLevelId={displayLevelId}
            isDisplayLeaf={isDisplayLeaf}
          />
        );
      }}
    </NestedCompositionTreemapDrilldownProvider>
  );
};

export const StackedBar = ({
  data,
  metric,
  hideLegend = false,
}: {
  data: BreakdownSingleDataSet | BreakdownTimeseriesDataSet;
  metric?: MetricValues;
  hideLegend?: boolean;
}) => {
  const { item } = useChart();
  const metricFromPlot = usePlotMetric();
  const metricToUse = metric ?? metricFromPlot;
  const { primaryFilter } = useTabContentConfig();
  const primaryDimension = primaryFilter?.id;
  const secondaryDimension = item?.plot?.secondaryDimension;
  const setHeaderBreadcrumb = useChartHeaderBreadcrumbSetter();
  const orientation = item?.plot?.orientation ?? 'horizontal';

  // Drilldown ships on the composition pages: a snapshot breakdown of a
  // drillable segment whose metric routes to composition_v3, with a chart
  // header listening for the breadcrumb (the expand modal and shared reports
  // have none and keep the static chart).
  if (
    setHeaderBreadcrumb !== null &&
    orientation === 'horizontal' &&
    data.type === DataType.BREAKDOWN_SINGLE &&
    isTreemapDrilldownSegment(secondaryDimension) &&
    primaryDimension !== undefined &&
    metricToUse !== undefined &&
    routesToComposition(metricToUse, 'stacked-bar')
  ) {
    return (
      <DrilldownStackedBar
        data={data}
        metric={metricToUse}
        hideLegend={hideLegend}
        secondaryDimension={secondaryDimension}
        primaryDimension={primaryDimension}
      />
    );
  }

  return <StackedBarView data={data} metric={metric} hideLegend={hideLegend} />;
};
